import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

import { useSession } from '@/hooks/useSession';
import { SOCKET_URL } from '@/config';

const socket = io(SOCKET_URL);

export const useChatSocket = (conversationId: string) => {
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const [feedback, setFeedback] = useState('');
  const nameRef = useRef('');
  const useridRef = useRef('');
  const imageRef = useRef('');
  const isTyping = useRef(false);

  const emitNewUser = (conversationId: string): Promise<void> => {
    return new Promise<void>((resolve) => {
      const userParams = {
        name: nameRef.current,
        gameid: conversationId,
        id: useridRef.current,
        image: imageRef.current,
      };
      socket.emit('chat:newUser', userParams, () => {
        resolve();
      });
    });
  };

  useEffect(() => {
    if (!session || !session.user) {
      return;
    }

    const { username, id, profile_photo } = session.user;
    nameRef.current = username;
    useridRef.current = id;
    imageRef.current = profile_photo;

    emitNewUser(conversationId);

    const receiveMessageHandler = () => {
      queryClient.invalidateQueries({
        queryKey: ['conversations'],
      });
    };

    const userTypingHandler = (username: string, isTyping: boolean) => {
      setFeedback(isTyping ? `${username} is typing...` : '');
    };

    socket.on('chat:receiveMessage', receiveMessageHandler);
    socket.on('chat:user-is-typing', userTypingHandler);

    return () => {
      socket.off('chat:receiveMessage', receiveMessageHandler);
      socket.off('chat:user-is-typing', userTypingHandler);
    };
  }, [conversationId, queryClient, session]);

  const sendMessageToServer = (message: string, conversationId: string) => {
    socket.emit('chat:sendMessage', {
      message,
      user: {
        name: nameRef.current,
        gameid: conversationId,
        id: useridRef.current,
        image: imageRef.current,
      },
    });
    queryClient.invalidateQueries({
      queryKey: ['conversations'],
    });
  };

  const emitTypingStatus = (isUserTyping: boolean) => {
    if (isTyping.current !== isUserTyping) {
      isTyping.current = isUserTyping;
      socket.emit('chat:istyping', {
        istyping: isUserTyping,
        gameid: conversationId,
      });
    }
  };

  return {
    feedback,
    nameRef,
    useridRef,
    imageRef,
    sendMessageToServer,
    emitTypingStatus,
    emitNewUser,
  };
};
